import { forwardRef, useId, type ReactNode } from 'react'

import { tv } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { Divider } from '../../atoms/divider'
import { Text } from '../../atoms/text'

const kpiCardVariants = tv(
  {
    slots: {
      root: 'bg-surface/50 shadow-dialog min-w-0 rounded-md p-2',
      frame:
        'bg-branding-white-50 shadow-dialog ring-surface flex min-w-0 flex-col gap-2 overflow-clip rounded-md pb-2 ring-1',
      header:
        'flex min-h-10 flex-wrap items-center justify-between gap-2 px-2 py-2',
      heading: 'flex min-w-0 max-w-full flex-auto items-center gap-2',
      icon: 'text-surface-title-50 inline-flex size-6 shrink-0 items-center justify-center',
      number:
        'bg-information-bg text-information-title ring-information-stroke inline-flex min-h-6 min-w-6 shrink-0 items-center justify-center rounded px-0.5 ring-[0.7px]',
      content:
        'bg-surface shadow-sticky-nav ring-background relative mx-2 flex min-w-0 flex-col gap-2 rounded-md p-2 ring-1',
      value: 'flex min-w-0 items-start gap-1',
      comparisons: 'flex flex-wrap items-center gap-0.5',
      comparison: 'inline-flex items-center gap-1',
      separator: 'text-surface-body inline-flex w-[18px] justify-center',
      chart: 'min-w-0'
    }
  },
  { twMerge: false }
)

export interface KpiCardComparison {
  id: string
  label: ReactNode
  icon?: ReactNode
}

export type KpiCardProps = Omit<React.ComponentPropsWithRef<'div'>, 'title'> & {
  title: ReactNode
  value: ReactNode
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  number?: ReactNode
  /** Compose with KpiCardAction, or supply another accessible control. */
  actionButton?: ReactNode
  /** Compose with KpiCardMoreAction, including a descriptive aria-label. */
  moreIcon?: ReactNode
  comparisons?: readonly KpiCardComparison[]
  showLeftIcon?: boolean
  showRightIcon?: boolean
  showNumber?: boolean
  showActionButton?: boolean
  showMoreIcon?: boolean
  /** The chart belongs to the consumer; this component does not invent data. */
  children?: ReactNode
}

const hasContent = (content: ReactNode): boolean =>
  content != null && typeof content !== 'boolean'

/**
 * Acqua Ømni, Figma 1488:17474. Its strokes sit outside the measured frames:
 * rings preserve those content widths and the 8px rhythm between layers.
 * The title keeps the source measurements at every viewport; the value
 * follows the h4 breakpoints (the source's 32px from 1025px to 1920px), so
 * a narrower viewport shrinks it as it does every other heading.
 * Optional slots retain numeric zero; visibility is controlled independently.
 */
export const KpiCard = forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      title,
      value,
      leftIcon,
      rightIcon,
      number,
      actionButton,
      moreIcon,
      comparisons,
      showLeftIcon = true,
      showRightIcon = true,
      showNumber = true,
      showActionButton = true,
      showMoreIcon = true,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const titleId = useId()
    const slots = kpiCardVariants()

    return (
      <div
        ref={ref}
        role='group'
        {...props}
        aria-labelledby={
          props['aria-labelledby'] ??
          (props['aria-label'] ? undefined : titleId)
        }
        data-slot='kpi-card'
        className={cn(slots.root(), className)}
      >
        <div data-slot='kpi-card-frame' className={slots.frame()}>
          <div>
            <div data-slot='kpi-card-header' className={slots.header()}>
              <div className={slots.heading()}>
                {showLeftIcon && hasContent(leftIcon) ? (
                  <span data-slot='kpi-card-left-icon' className={slots.icon()}>
                    {leftIcon}
                  </span>
                ) : null}
                <Text
                  id={titleId}
                  as='div'
                  variant='body-lg'
                  weight='semibold'
                  truncate
                  className='text-surface-title-50 min-w-0 text-[16px]/[24px]'
                >
                  {title}
                </Text>
                {showRightIcon && hasContent(rightIcon) ? (
                  <span
                    data-slot='kpi-card-right-icon'
                    className={slots.icon()}
                  >
                    {rightIcon}
                  </span>
                ) : null}
                {showNumber && hasContent(number) ? (
                  <span data-slot='kpi-card-number' className={slots.number()}>
                    <Text
                      as='span'
                      variant='caption-lg'
                      weight='semibold'
                      className='w-full text-center text-[12px]/[18px]'
                    >
                      {number}
                    </Text>
                  </span>
                ) : null}
              </div>
              {showActionButton && hasContent(actionButton) ? (
                <div
                  data-slot='kpi-card-action-button'
                  className='max-w-full shrink-0'
                >
                  {actionButton}
                </div>
              ) : null}
            </div>
            <Divider />
          </div>
          <div data-slot='kpi-card-content' className={slots.content()}>
            <div className='flex min-w-0 flex-col gap-1'>
              <div className={slots.value()}>
                <Text
                  as='div'
                  variant='h4'
                  weight='medium'
                  className='text-surface-title-50 min-w-0 flex-1 break-words tracking-normal'
                >
                  {value}
                </Text>
                {showMoreIcon && hasContent(moreIcon) ? (
                  <span data-slot='kpi-card-more-icon' className={slots.icon()}>
                    {moreIcon}
                  </span>
                ) : null}
              </div>
              {comparisons?.length ? (
                <div
                  data-slot='kpi-card-comparisons'
                  className={slots.comparisons()}
                >
                  {comparisons.map((comparison, index) => (
                    <span key={comparison.id} className={slots.comparison()}>
                      {index > 0 ? (
                        <Text
                          as='span'
                          variant='caption-lg'
                          weight='semibold'
                          aria-hidden='true'
                          className={cn(
                            slots.separator(),
                            'text-[12px]/[18px]'
                          )}
                        >
                          •
                        </Text>
                      ) : null}
                      <span className='inline-flex items-center gap-1'>
                        <Text
                          as='span'
                          variant='caption-lg'
                          weight='semibold'
                          className='text-surface-body text-[12px]/[18px]'
                        >
                          {comparison.label}
                        </Text>
                        {comparison.icon}
                      </span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            {hasContent(children) ? (
              <div data-slot='kpi-card-chart' className={slots.chart()}>
                {children}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  }
)
KpiCard.displayName = 'KpiCard'
